/**
 * Sistema de debug e tracing entre actions
 * 
 * Rastreia chamadas entre actions para facilitar debugging e monitoramento
 * Mantém call stack, timing e contexto de correlação
 */

import { sendToTerminal } from '../../terminalLogger';
import { LogCategory } from '@/config/logging';

/**
 * Contexto de uma chamada entre actions
 */
export interface ActionCallContext {
  caller: string;  // 'SaveService.saveFile'
  callee: string;  // 'RequestService.cancel'
  params?: any;
  timestamp: number;
  correlationId?: string;
  requestId?: string;
}

/**
 * Classe para rastreamento de chamadas entre actions
 */
class ActionDebugger {
  private callStack: ActionCallContext[] = [];
  private readonly maxStackSize = 50;

  /**
   * Registra uma chamada entre actions
   * 
   * @param caller Nome do action/método que está chamando (ex: 'SaveService.saveFile')
   * @param callee Nome do action/método sendo chamado (ex: 'RequestService.cancel')
   * @param params Parâmetros da chamada (opcional)
   * @param context Contexto adicional (correlationId, requestId, etc.)
   */
  logActionCall(
    caller: string,
    callee: string,
    params?: any,
    context?: { correlationId?: string; requestId?: string }
  ): void {
    const callContext: ActionCallContext = {
      caller,
      callee,
      params: this.sanitizeParams(params),
      timestamp: Date.now(),
      correlationId: context?.correlationId,
      requestId: context?.requestId
    };

    // Adicionar ao call stack
    this.callStack.push(callContext);
    
    // Limitar tamanho do stack
    if (this.callStack.length > this.maxStackSize) {
      this.callStack.shift();
    }

    // Logar a chamada
    sendToTerminal(
      `🔗 [ACTION_CALL] ${caller} → ${callee}`,
      {
        caller,
        callee,
        params: callContext.params,
        stackDepth: this.callStack.length,
        correlationId: callContext.correlationId,
        requestId: callContext.requestId,
        timestamp: new Date(callContext.timestamp).toISOString()
      },
      LogCategory.BUSINESS
    );
  }

  /**
   * Rastreia entrada e saída de um método de action
   * 
   * @param serviceName Nome do serviço (ex: 'SaveService')
   * @param methodName Nome do método (ex: 'saveFile')
   * @param fn Função a ser executada
   * @param context Contexto adicional (opcional)
   * @returns Resultado da função
   */
  async trace<T>(
    serviceName: string,
    methodName: string,
    fn: () => Promise<T>,
    context?: { correlationId?: string; requestId?: string }
  ): Promise<T> {
    const fullMethodName = `${serviceName}.${methodName}`;
    const startTime = Date.now();
    
    sendToTerminal(
      `🔵 [${serviceName}] Iniciando ${methodName}`,
      {
        service: serviceName,
        method: methodName,
        correlationId: context?.correlationId,
        requestId: context?.requestId,
        timestamp: new Date().toISOString()
      },
      LogCategory.BUSINESS
    );

    try {
      const result = await fn();
      const elapsedMs = Date.now() - startTime;

      sendToTerminal(
        `✅ [${serviceName}] ${methodName} concluído`,
        {
          service: serviceName,
          method: methodName,
          elapsedMs,
          correlationId: context?.correlationId,
          requestId: context?.requestId,
          timestamp: new Date().toISOString()
        },
        LogCategory.BUSINESS
      );

      return result;
    } catch (error) {
      const elapsedMs = Date.now() - startTime;
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorStack = error instanceof Error ? error.stack : undefined;

      sendToTerminal(
        `❌ [${serviceName}] Erro em ${methodName}`,
        {
          service: serviceName,
          method: methodName,
          error: errorMessage,
          stack: errorStack,
          elapsedMs,
          correlationId: context?.correlationId,
          requestId: context?.requestId,
          timestamp: new Date().toISOString()
        },
        LogCategory.ERROR
      );

      throw error;
    }
  }

  /**
   * Versão síncrona do trace
   */
  traceSync<T>(
    serviceName: string,
    methodName: string,
    fn: () => T,
    context?: { correlationId?: string; requestId?: string }
  ): T {
    const fullMethodName = `${serviceName}.${methodName}`;
    const startTime = Date.now();
    
    sendToTerminal(
      `🔵 [${serviceName}] Iniciando ${methodName}`,
      {
        service: serviceName,
        method: methodName,
        correlationId: context?.correlationId,
        requestId: context?.requestId,
        timestamp: new Date().toISOString()
      },
      LogCategory.BUSINESS
    );

    try {
      const result = fn();
      const elapsedMs = Date.now() - startTime;

      sendToTerminal(
        `✅ [${serviceName}] ${methodName} concluído`,
        {
          service: serviceName,
          method: methodName,
          elapsedMs,
          correlationId: context?.correlationId,
          requestId: context?.requestId,
          timestamp: new Date().toISOString()
        },
        LogCategory.BUSINESS
      );

      return result;
    } catch (error) {
      const elapsedMs = Date.now() - startTime;
      const errorMessage = error instanceof Error ? error.message : String(error);
      const errorStack = error instanceof Error ? error.stack : undefined;

      sendToTerminal(
        `❌ [${serviceName}] Erro em ${methodName}`,
        {
          service: serviceName,
          method: methodName,
          error: errorMessage,
          stack: errorStack,
          elapsedMs,
          correlationId: context?.correlationId,
          requestId: context?.requestId,
          timestamp: new Date().toISOString()
        },
        LogCategory.ERROR
      );

      throw error;
    }
  }

  /**
   * Obtém o call stack atual
   */
  getCallStack(): ActionCallContext[] {
    return [...this.callStack];
  }

  /**
   * Limpa o call stack
   */
  clearCallStack(): void {
    this.callStack = [];
  }

  /**
   * Sanitiza parâmetros removendo dados sensíveis
   */
  private sanitizeParams(params: any): any {
    if (!params || typeof params !== 'object') {
      return params;
    }

    const sanitized = { ...params };
    const sensitiveFields = ['password', 'senha', 'token', 'secret', 'key', 'hmac', 'signature'];

    for (const field of sensitiveFields) {
      if (field in sanitized) {
        sanitized[field] = '[REDACTED]';
      }
    }

    return sanitized;
  }
}

/**
 * Instância singleton do ActionDebugger
 */
export const actionDebugger = new ActionDebugger();

